/* 2009-2018 Copyright Ridecell Inc. All rights reserved. */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { IntlProvider, addLocaleData } from 'react-intl'
import { Broadcast } from 'react-broadcast'
import Immutable from 'immutable'
import axios from 'axios'
import en from 'react-intl/locale-data/en'
import es from 'react-intl/locale-data/es'
import it from 'react-intl/locale-data/it'
import fr from 'react-intl/locale-data/fr'
import sv from 'react-intl/locale-data/sv'

import config from '../config/config'
import navigator from '../util/navigator'
import window from '../util/window'

addLocaleData([...en, ...es, ...fr, ...it, ...sv])
const baseURL = 'https://api.phraseapp.com/api/v2/projects'
const localeMap = {
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  it: 'it-IT',
  sv: 'sv-SE',
}

export class BundleProvider extends Component {
  static propTypes = {
    accessToken: PropTypes.string,
    asyncStorage: PropTypes.object,
    children: PropTypes.node,
    defaultLocale: PropTypes.string,
    forceLocale: PropTypes.string,
    projectID: PropTypes.array,
  }

  constructor(props, context = {}) {
    super(props, context)

    this.state = {
      accessToken: props.accessToken || config.accessToken,
      currLocale: null,
      isLoading: true,
    }

    this.loadedMessages = Immutable.Map()
  }

  async componentDidMount() {
    this.initSessionStorage()
    const { defaultLocale, forceLocale, asyncStorage } = this.props
    const supportedLocales = await this.getSupportedLocales()
    const pickedLocale = !asyncStorage ? this.pickBrowserLocale(supportedLocales) : null

    let currLocale = ''
    if (forceLocale) {
      currLocale = forceLocale
    } else if (pickedLocale !== null) {
      currLocale = pickedLocale
    } else {
      currLocale = defaultLocale
    }

    // This is valid according to React docs, rule is outdated
    // https://github.com/yannickcr/eslint-plugin-react/issues/1754
    // eslint-disable-next-line
    await this.fetchBundle(currLocale, supportedLocales)
  }

  getSupportedLocales = async () => {
    const { projectID } = this.props
    const { accessToken } = this.state

    if (projectID) {
      await Promise.all(
        projectID.map(id =>
          axios
            .get(`${baseURL}/${id}/locales?file_format=react_simple_json`, {
              headers: { Authorization: `token ${accessToken}` },
            })
            .then(res => {
              this.setState({ [`supportedLocales_${id}`]: res.data })
            })
            .catch(error => {
              console.error('Locale Fetch Failed', error)
              return null
            })
        )
      )

      return {
        [`supportedLocales_${projectID[0]}`]: this.state[`supportedLocales_${projectID[0]}`],
        [`supportedLocales_${projectID[1]}`]: this.state[`supportedLocales_${projectID[1]}`],
      }
    }

    return null
  }

  getSessionStorage = async () => {
    const { asyncStorage } = this.props

    if (asyncStorage && asyncStorage.getItem) {
      const bundlesAsyncStorage = await asyncStorage.getItem('bundles')

      if (bundlesAsyncStorage && Object.keys(bundlesAsyncStorage).length) {
        return JSON.parse(bundlesAsyncStorage)
      }
    } else {
      if (window.sessionStorage.getItem('bundles')) {
        return JSON.parse(window.sessionStorage.getItem('bundles'))
      }
    }
    return {}
  }

  setSessionStorage = async bundles => {
    const { asyncStorage } = this.props

    if (asyncStorage && asyncStorage.setItem) {
      asyncStorage.setItem('bundles', JSON.stringify(bundles))
    } else {
      if (window.sessionStorage.getItem('bundles')) {
        window.sessionStorage.setItem('bundles', JSON.stringify(bundles))
      }
    }
  }

  initSessionStorage = async () => {
    const { asyncStorage } = this.props

    if (asyncStorage && asyncStorage.getItem) {
      const bundlesAsyncStorage = await asyncStorage.getItem('bundles')
      if (!bundlesAsyncStorage) {
        asyncStorage.setItem('bundles', JSON.stringify({}))
      }
    } else {
      if (!window.sessionStorage.getItem('bundles')) {
        window.sessionStorage.setItem('bundles', JSON.stringify({}))
      }
    }
  }

  addSessionBundle = () => {
    const { projectID } = this.props
    const { currLocale } = this.state
    const sessionStorage = this.getSessionStorage()
    const bundle = Object.assign(
      {},
      this.state[`bundle_${projectID[0]}`],
      this.state[`bundle_${projectID[1]}`]
    )

    const newSession = { ...sessionStorage, [currLocale]: { bundle } }
    this.setSessionStorage(newSession)
    this.setState({ bundle })
  }

  pickBrowserLocale = supportedLocales => {
    const { projectID } = this.props

    if (supportedLocales) {
      const navLanguage = navigator.languages[0]
      let matchLanguage
      let key

      if (navLanguage.length === 2) {
        matchLanguage = localeMap[navLanguage]
      } else {
        matchLanguage = navLanguage
      }

      for (let i = 0; i < supportedLocales[`supportedLocales_${projectID[0]}`].length; i += 1) {
        if (
          supportedLocales[`supportedLocales_${projectID[0]}`][i].name.toLowerCase() ===
          matchLanguage.toLowerCase()
        ) {
          key = matchLanguage
        }
      }

      return key
    }
    return null
  }

  fetchBundle = async (currLocale, supportedLocales) => {
    const { projectID } = this.props
    const { accessToken } = this.state

    await Promise.all(
      projectID.map(async (id, index) => {
        let newLocale = supportedLocales[`supportedLocales_${projectID[index]}`].find(
          sl => sl.name === currLocale
        )
        if (
          !supportedLocales[`supportedLocales_${projectID[index]}`].some(
            sl => sl.name === newLocale.name
          )
        ) {
          // Default to English when detected locale isn't supported
          newLocale = supportedLocales[`supportedLocales_${projectID[index]}`].find(
            sl => sl.name === 'en-US'
          )
        }
        return axios
          .get(`${baseURL}/${id}/locales/${newLocale.id}/download?file_format=react_simple_json`, {
            headers: {
              Authorization: `token ${accessToken}`,
              ['Cache-Control']: 'no-cache',
            },
          })
          .then(res => {
            const resDataLength = Object.keys(res.data).length
            if (resDataLength !== 0) {
              this.setState({
                [`bundle_${id}`]: res.data,
                currLocale,
                isLoading: false,
              })
            }
          })
          .catch(error => {
            console.error('Bundle Fetch Failed', error)
          })
      })
    )

    this.addSessionBundle()
  }

  render() {
    const { children, defaultLocale } = this.props
    let { bundle, currLocale } = this.state
    if (!currLocale) {
      currLocale = defaultLocale
    }
    if (!bundle) {
      bundle = {}
    }
    return (
      <IntlProvider defaultLocale={defaultLocale} locale={currLocale} messages={bundle}>
        <Broadcast channel="resourceBundleMessages" value={this.loadedMessages}>
          {children}
        </Broadcast>
      </IntlProvider>
    )
  }
}

export default BundleProvider
